import { useState, useEffect } from 'react';
import { useTimerStore } from '../stores/timerStore';
import { ClockIcon, StopIcon, PauseIcon, PlayIcon } from '@heroicons/react/24/outline';

const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

interface ActiveTimerBarProps {
  onStop?: (data: {
    projectId: string;
    activityId: string;
    description: string;
    elapsedSeconds: number;
    totalBreakDuration: number;
  }) => void;
}

export default function ActiveTimerBar({ onStop }: ActiveTimerBarProps) {
  const {
    isRunning,
    isPaused,
    projectId,
    activityId,
    description,
    getElapsedSeconds,
    getNetWorkingSeconds,
    stopTimer,
    pauseTimer,
    resumeTimer
  } = useTimerStore();

  const [displayTime, setDisplayTime] = useState(0);
  const [netWorkingTime, setNetWorkingTime] = useState(0);

  useEffect(() => {
    if (isRunning) {
      document.body.style.paddingBottom = '100px';
    } else {
      document.body.style.paddingBottom = '0';
    }

    return () => {
      document.body.style.paddingBottom = '0';
    };
  }, [isRunning]);

  useEffect(() => {
    if (!isRunning) return;

    setDisplayTime(getElapsedSeconds());
    setNetWorkingTime(getNetWorkingSeconds());

    const interval = setInterval(() => {
      setDisplayTime(getElapsedSeconds());
      setNetWorkingTime(getNetWorkingSeconds());
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning, isPaused, getElapsedSeconds, getNetWorkingSeconds]);

  const handleStop = () => {
    const { elapsedSeconds, totalBreakDuration } = stopTimer();
    
    // Defensive check: projectId en activityId moeten bestaan
    if (!projectId || !activityId) {
      console.warn('Timer stopped without project/activity. This should not happen in normal flow.');
      return;
    }
    
    // Roep callback aan
    if (onStop) {
      onStop({
        projectId,
        activityId,
        description,
        elapsedSeconds,
        totalBreakDuration,
      });
    }
  };

  const handlePauseResume = () => {
    if (isPaused) {
      resumeTimer();
    } else {
      pauseTimer();
    }
  };

  if (!isRunning) {
    return null;
  }

  return (
    <div className={`fixed bottom-0 left-0 right-0 shadow-lg z-40 transition-colors ${
      isPaused ? 'bg-yellow-600' : 'bg-blue-600'
    } text-white`}>
      <div className="container mx-auto px-4 py-3">
        <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <ClockIcon className={`h-6 w-6 ${isPaused ? '' : 'animate-pulse'}`} />
            <div>
              <p className="text-sm font-medium">
                {isPaused ? 'Timer gepauzeerd' : 'Timer actief'}
              </p>
              <p className="text-xs opacity-90">{description || 'Geen omschrijving'}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="text-center sm:text-right">
              <div className="text-xl sm:text-2xl font-mono font-bold">
                {formatTime(displayTime)}
              </div>
              <div className="text-xs opacity-90">
                Netto werktijd: {formatTime(netWorkingTime)}
              </div>
            </div>

            <button
              onClick={handlePauseResume}
              className="bg-white text-blue-600 px-3 py-2 sm:px-4 rounded-lg font-medium hover:bg-blue-50 transition-colors flex items-center space-x-1 sm:space-x-2"
            >
              {isPaused ? (
                <>
                  <PlayIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden sm:inline">Hervatten</span>
                  <span className="sm:hidden">Hervat</span>
                </>
              ) : (
                <>
                  <PauseIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Pauze</span>
                </>
              )}
            </button>

            <button
              onClick={handleStop}
              className="bg-red-600 text-white px-3 py-2 sm:px-4 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center space-x-1 sm:space-x-2"
            >
              <StopIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Stop</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}